import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Trash2, Settings as SettingsIcon } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useBusinessId } from '../../hooks/useBusinessId';
import { toast } from 'sonner';
import SelectServiceModal from '../../components/manage/SelectServiceModal';
import ModifierModal from '../../components/ModifierModal';
import { Service, ServiceCategory } from '../../lib/services';
import { useBusiness } from '../../contexts/BusinessContext';
import { Switch } from '@headlessui/react';
import BusinessLogoUpload from '../../components/BusinessLogoUpload';
import { getSiteUrl } from '../../lib/siteConfig';

interface SubCategory {
  id: string;
  title: string;
  services: Array<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  }>;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  category_title: string;
  sub_categories?: SubCategory[];
}

export default function OnlineBookings() {
  const navigate = useNavigate();
  const supabase = useSupabase();
  const businessId = useBusinessId();
  const { selectedBusiness, updateBusiness } = useBusiness();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
  const [subCategoryTitle, setSubCategoryTitle] = useState("");
  const [isAddingToSubCategory, setIsAddingToSubCategory] = useState(false);
  const [isAssignServicesModalOpen, setIsAssignServicesModalOpen] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  
  // New state variables for color picker
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [navbarColor, setNavbarColor] = useState('#000000'); // Default black color
  const [logoInvert, setLogoInvert] = useState(false); // Logo inversion for dark backgrounds
  

  
  // Buffer time state - start with 0 until loaded from database
  const [bufferTime, setBufferTime] = useState<number>(0); // Start with 0, load from database
  const [bufferTimeLoaded, setBufferTimeLoaded] = useState<boolean>(false); // Track if we've loaded from DB

  // Popup settings state
  const [popupSettings, setPopupSettings] = useState({
    popup_enabled: false,
    popup_delay_seconds: 10,
    popup_duration_seconds: 5,
    popup_text: 'Welcome! Book your appointment today and get 10% off your first service.',
    popup_button_text: 'Book Now',
    popup_button_action: 'url' as 'url' | 'lead_form',
    popup_button_url: '',
    popup_send_email: false,
    popup_email_template_id: '',
    call_button_enabled: false
  });
  const [popupSettingsLoaded, setPopupSettingsLoaded] = useState<boolean>(false);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState<Array<{id: string; name: string}>>([]);

  // New state variables for service info modal
  const [isServiceInfoModalOpen, setIsServiceInfoModalOpen] = useState(false);
  const [selectedServiceInfo, setSelectedServiceInfo] = useState<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  } | null>(null);

  // New state for category editing view
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState<Category | null>(null);

  // Color picker modal state for consistency with LeadForms
  const [colorPickerModal, setColorPickerModal] = useState<{isOpen: boolean, type: 'navbar' | null, currentColor: string}>({
    isOpen: false,
    type: null,
    currentColor: '#000000'
  });

  // Modifier modal state
  const [isModifierModalOpen, setIsModifierModalOpen] = useState(false);
  const [selectedServiceForModifier, setSelectedServiceForModifier] = useState<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  } | null>(null);

  // Functions for category editing view
  const handleCategorySelect = (category: Category) => {
    setSelectedCategoryForEdit(category);
  };

  const handleBackToList = () => {
    setSelectedCategoryForEdit(null);
  };

  // Modifier modal functions
  const handleOpenModifierModal = (service: {
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  }) => {
    setSelectedServiceForModifier(service);
    setIsModifierModalOpen(true);
  };

  const handleCloseModifierModal = () => {
    setIsModifierModalOpen(false);
    setSelectedServiceForModifier(null);
  };

  // Color picker modal functions (matching LeadForms pattern)
  const openColorPicker = (type: 'navbar', currentColor: string) => {
    setColorPickerModal({
      isOpen: true,
      type,
      currentColor
    });
  };

  const closeColorPicker = () => {
    setColorPickerModal({
      isOpen: false,
      type: null,
      currentColor: '#000000'
    });
  };

  const handleColorSelect = (color: string) => {
    if (!colorPickerModal.type) return;

    if (colorPickerModal.type === 'navbar') {
      setNavbarColor(color);
      handleSaveNavbarColor();
    }
    
    closeColorPicker();
  };

  // Predefined color palette (matching LeadForms)
  const colorPalette = [
    '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#f3f4f6', '#ffffff',
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981',
    '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
    '#ec4899', '#f43f5e', '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d',
    '#16a34a', '#059669', '#0891b2', '#0284c7', '#2563eb', '#4f46e5', '#7c3aed',
    '#9333ea', '#c026d3', '#db2777', '#e11d48'
  ];

    // Helper function to update both categories and selectedCategoryForEdit
  const updateCategoriesAndSelected = (updatedCategories: Category[]) => {
    setCategories(updatedCategories);
    
    // Update selectedCategoryForEdit if it's currently selected
    if (selectedCategoryForEdit) {
      const updatedCategory = updatedCategories.find(cat => cat.id === selectedCategoryForEdit.id);
      if (updatedCategory) {
        setSelectedCategoryForEdit(updatedCategory);
      }
    }
  };

  const handleToggleOnlineBookings = async (enabled: boolean) => {
    if (!selectedBusiness) return;

    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        allow_online_bookings: enabled
      };

      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });

      toast.success(`Online bookings ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating online bookings status:', error);
      toast.error('Failed to update online bookings status');
    }
  };

  // Fetch existing categories on component mount
  useEffect(() => {
    if (businessId) {
      fetchCategories();
    }
  }, [businessId]);

  // Load navbar color and logo invert from business settings
  useEffect(() => {
    if (selectedBusiness?.booking_settings?.navbar_color) {
      setNavbarColor(selectedBusiness.booking_settings.navbar_color);
    }
    if (selectedBusiness?.booking_settings?.logo_invert !== undefined) {
      setLogoInvert(selectedBusiness.booking_settings.logo_invert);
    }
  }, [selectedBusiness]);



  // Load buffer time from online_booking_settings
  useEffect(() => {
    if (businessId) {
      fetchBufferTime();
      fetchEmailTemplates();
    }
  }, [businessId]);

  const fetchEmailTemplates = async () => {
    if (!supabase || !businessId) return;
    
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, name')
        .eq('business_id', businessId)
        .order('name');
      
      if (error) {
        console.error('Error fetching email templates:', error);
        return;
      }
      
      setEmailTemplates(data || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchBufferTime = async () => {
    if (!supabase || !businessId) return;
    
    console.log(`[BUFFER TIME] Fetching buffer time and popup settings for business ${businessId}`);
    
    try {
      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('buffer_time, popup_enabled, popup_delay_seconds, popup_duration_seconds, popup_text, popup_button_text, popup_button_action, popup_button_url, popup_send_email, popup_email_template_id, call_button_enabled')
        .eq('business_id', businessId)
        .single();
      
      console.log('[BUFFER TIME] Fetch result:', data, error);
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found - explicitly set defaults and mark as loaded
          console.log('[BUFFER TIME] No settings found in database, setting defaults');
          setBufferTime(0);
          setBufferTimeLoaded(true);
          setPopupSettingsLoaded(true);
        } else {
          console.error('[BUFFER TIME] Error fetching settings:', error);
          // On other errors, still mark as loaded but keep defaults
          setBufferTimeLoaded(true);
          setPopupSettingsLoaded(true);
        }
        return;
      }
      
      // Set buffer time
      if (data?.buffer_time !== undefined && data?.buffer_time !== null) {
        console.log(`[BUFFER TIME] Setting buffer time to ${data.buffer_time} minutes from database`);
        setBufferTime(data.buffer_time);
      } else {
        console.log('[BUFFER TIME] Buffer time value in database is undefined or null, setting to 0');
        setBufferTime(0);
      }
      
      // Set popup settings
      setPopupSettings({
        popup_enabled: data?.popup_enabled || false,
        popup_delay_seconds: data?.popup_delay_seconds || 10,
        popup_duration_seconds: data?.popup_duration_seconds || 5,
        popup_text: data?.popup_text || 'Welcome! Book your appointment today and get 10% off your first service.',
        popup_button_text: data?.popup_button_text || 'Book Now',
        popup_button_action: (data?.popup_button_action as 'url' | 'lead_form') || 'url',
        popup_button_url: data?.popup_button_url || '',
        popup_send_email: data?.popup_send_email || false,
        popup_email_template_id: data?.popup_email_template_id || '',
        call_button_enabled: data?.call_button_enabled || false
      });
      
      setBufferTimeLoaded(true);
      setPopupSettingsLoaded(true);
    } catch (error) {
      console.error('[BUFFER TIME] Error fetching settings:', error);
      setBufferTimeLoaded(true);
      setPopupSettingsLoaded(true);
    }
  };

  const handleBufferTimeChange = async (minutes: number) => {
    if (!supabase || !businessId) return;
    
    try {
      // Update UI immediately
      setBufferTime(minutes);
      console.log(`[BUFFER TIME] Setting buffer time to ${minutes} minutes for business ${businessId}`);
      
      // Use upsert pattern (on conflict do update) for more reliable operation
      const { data, error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { 
            business_id: businessId, 
            buffer_time: minutes 
          },
          { 
            onConflict: 'business_id'
          }
        )
        .select();
      
      console.log('[BUFFER TIME] Upsert result:', data, error);
      
      if (error) {
        throw error;
      }
      
      toast.success(`Buffer time set to ${minutes} minutes`);
    } catch (error) {
      console.error('[BUFFER TIME] Error saving buffer time:', error);
      toast.error('Failed to save buffer time. Please try again.');
    }
  };

  // Popup settings handlers
  const handlePopupToggle = async (enabled: boolean) => {
    if (!supabase || !businessId) return;
    
    try {
      const newSettings = { ...popupSettings, popup_enabled: enabled };
      setPopupSettings(newSettings);
      
      const { error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { 
            business_id: businessId, 
            popup_enabled: enabled,
            popup_delay_seconds: newSettings.popup_delay_seconds,
            popup_duration_seconds: newSettings.popup_duration_seconds,
            popup_text: newSettings.popup_text,
            popup_button_text: newSettings.popup_button_text,
            popup_button_action: newSettings.popup_button_action,
            popup_button_url: newSettings.popup_button_url,
            popup_send_email: newSettings.popup_send_email,
            popup_email_template_id: newSettings.popup_email_template_id,
            call_button_enabled: newSettings.call_button_enabled
          },
          { 
            onConflict: 'business_id'
          }
        );
      
      if (error) throw error;
      
      toast.success(`Popup window ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error saving popup settings:', error);
      toast.error('Failed to save popup settings. Please try again.');
    }
  };

  const handlePopupSettingChange = async (setting: string, value: number | string) => {
    if (!supabase || !businessId) return;
    
    try {
      // Convert string booleans to actual booleans for specific settings
      let processedValue: any = value;
      if (setting === 'popup_send_email' || setting === 'call_button_enabled') {
        processedValue = value === 'true' || value === true;
      }
      
      const newSettings = { ...popupSettings, [setting]: processedValue };
      setPopupSettings(newSettings);
      
      const { error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { 
            business_id: businessId, 
            popup_enabled: newSettings.popup_enabled,
            popup_delay_seconds: newSettings.popup_delay_seconds,
            popup_duration_seconds: newSettings.popup_duration_seconds,
            popup_text: newSettings.popup_text,
            popup_button_text: newSettings.popup_button_text,
            popup_button_action: newSettings.popup_button_action,
            popup_button_url: newSettings.popup_button_url,
            popup_send_email: newSettings.popup_send_email,
            popup_email_template_id: newSettings.popup_email_template_id,
            call_button_enabled: newSettings.call_button_enabled
          },
          { 
            onConflict: 'business_id'
          }
        );
      
      if (error) throw error;
      
      toast.success('Popup settings updated');
    } catch (error) {
      console.error('Error saving popup settings:', error);
      toast.error('Failed to save popup settings. Please try again.');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('online_booking_flow')
        .select('id, category_title, sub_categories')
        .eq('business_id', businessId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setCategories(data || []);
      
      // Update selectedCategoryForEdit if it's currently selected
      if (selectedCategoryForEdit) {
        const updatedCategory = data?.find(cat => cat.id === selectedCategoryForEdit.id);
        if (updatedCategory) {
          setSelectedCategoryForEdit(updatedCategory);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryTitle.trim() || !businessId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('online_booking_flow')
        .insert([{
          business_id: businessId,
          category_title: categoryTitle.trim()
        }])
        .select()
        .single();

      if (error) throw error;

      setCategories([...categories, data]);
      setIsModalOpen(false);
      setCategoryTitle('');
      toast.success('Category created successfully');
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to create category');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlusClick = (categoryId: string, isSubCategoryPlus: boolean = false) => {
    setSelectedCategoryId(categoryId);
    setIsAddingToSubCategory(isSubCategoryPlus);
    setIsOptionsModalOpen(true);
  };

  const handleAddSubCategory = async () => {
    if (!subCategoryTitle.trim() || !selectedCategoryId) {
      toast.error("Please enter a title for the sub-category");
      return;
    }

    try {
      // Get the current category to access its existing sub-categories
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      // Create new sub-category object
      const newSubCategory = {
        id: crypto.randomUUID(),
        title: subCategoryTitle.trim(),
        services: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Combine existing sub-categories with new one
      const updatedSubCategories = [
        ...(currentCategory?.sub_categories || []),
        newSubCategory
      ];

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Refresh categories to get latest data
      await fetchCategories();

      setSubCategoryTitle("");
      setIsSubCategoryModalOpen(false);
      toast.success("Sub-category added successfully");
    } catch (error) {
      console.error('Error adding sub-category:', error);
      toast.error('Failed to add sub-category');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('online_booking_flow')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      // Refresh categories to get latest data
      await fetchCategories();
      
      toast.success("Category deleted successfully");
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleDeleteSubCategory = async (subCategory: SubCategory) => {
    try {
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      const updatedSubCategories = currentCategory?.sub_categories?.filter((sc: SubCategory) => sc.id !== subCategory.id) || [];

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Refresh categories to get latest data
      await fetchCategories();
      
      toast.success("Sub-category deleted successfully");
    } catch (error) {
      console.error('Error deleting sub-category:', error);
      toast.error('Failed to delete sub-category');
    }
  };

  const handleServiceAdd = async (service: Service, priceOption: any) => {
    if (!selectedSubCategory || !selectedCategoryId || !businessId) return;

    try {
      // Get the current category to access its existing sub-categories
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      // Check if service is already assigned
      const isServiceAssigned = currentCategory?.sub_categories?.some((subCat: SubCategory) => 
        subCat.id === selectedSubCategory.id && 
        subCat.services?.some((s: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => 
          s.service.id === service.id && s.priceOption.id === priceOption.id
        )
      );

      if (isServiceAssigned) {
        toast.error('This service is already assigned to this sub-category');
        return;
      }

      // Update the sub-categories array with the new service
      const updatedSubCategories = currentCategory?.sub_categories?.map((subCat: SubCategory) => {
        if (subCat.id === selectedSubCategory.id) {
          return {
            ...subCat,
            services: [
              ...(subCat.services || []),
              {
                service: service,
                priceOption: priceOption
              }
            ]
          };
        }
        return subCat;
      });

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Update local UI state
      const updatedCategories = categories.map(category => {
        if (category.id === selectedCategoryId) {
          const updatedCategory = {
            ...category,
            sub_categories: updatedSubCategories
          };
          
          // Also update selectedCategoryForEdit if it's the same category
          if (selectedCategoryForEdit && selectedCategoryForEdit.id === selectedCategoryId) {
            setSelectedCategoryForEdit(updatedCategory);
          }
          
          return updatedCategory;
        }
        return category;
      });

      setCategories(updatedCategories);
      toast.success('Service assigned successfully');
    } catch (error) {
      console.error('Error assigning service:', error);
      toast.error('Failed to assign service');
    }
  };

  const handleDeleteService = async (subCategory: SubCategory, service: Service) => {
    try {
      // Find the category that contains this sub-category
      const category = categories.find(cat => 
        cat.sub_categories?.some(sc => sc.id === subCategory.id)
      );

      if (!category) {
        throw new Error('Category not found');
      }

      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', category.id)
        .single();

      if (fetchError) throw fetchError;

      const updatedSubCategories = currentCategory?.sub_categories?.map((sc: SubCategory) => {
        if (sc.id === subCategory.id) {
          return {
            ...sc,
            services: (sc.services || []).filter((s: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => s.service.id !== service.id)
          };
        }
        return sc;
      });

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', category.id);

      if (updateError) throw updateError;

      // Update local UI state
      const updatedCategories = categories.map(cat => {
        if (cat.id === category.id) {
          const updatedCategory = {
            ...cat,
            sub_categories: updatedSubCategories
          };
          
          // Also update selectedCategoryForEdit if it's the same category
          if (selectedCategoryForEdit && selectedCategoryForEdit.id === category.id) {
            setSelectedCategoryForEdit(updatedCategory);
          }
          
          return updatedCategory;
        }
        return cat;
      });

      setCategories(updatedCategories);
      toast.success('Service removed successfully');
    } catch (error) {
      console.error('Error removing service:', error);
      toast.error('Failed to remove service');
    }
  };

  const handleSubCategoryDrop = (subCat: SubCategory) => {
    // ... existing code ...
  };

  const handleServiceDrop = (service: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => {
    // ... existing code ...
  };

  const handleSubCategoryDragStart = (subCat: SubCategory) => {
    // ... existing code ...
  };

  const handleCopyUrl = async () => {
    if (!selectedBusiness?.slug) return;
    
          const url = `${getSiteUrl()}/${selectedBusiness.slug}/book`;
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      toast.error('Failed to copy URL');
    }
  };

  const handleSaveNavbarColor = async () => {
    if (!selectedBusiness) return;
    
    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        navbar_color: navbarColor
      };
      
      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });
      
      setIsColorPickerOpen(false);
      toast.success('Navigation bar color updated');
    } catch (error) {
      console.error('Error updating navbar color:', error);
      toast.error('Failed to update navigation bar color');
    }
  };

  const handleSaveLogoInvert = async (newLogoInvert?: boolean) => {
    if (!selectedBusiness) {
      console.log('No selected business, cannot save logo invert');
      return;
    }
    
    const logoInvertValue = newLogoInvert !== undefined ? newLogoInvert : logoInvert;
    console.log('Saving logo invert with value:', logoInvertValue);
    
    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        logo_invert: logoInvertValue
      };
      
      console.log('Updated settings:', updatedSettings);
      
      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });
      
      console.log('Logo invert saved successfully');
      toast.success('Logo style updated');
    } catch (error) {
      console.error('Error updating logo style:', error);
      toast.error('Failed to update logo style');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 relative z-0" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      {/* Main Content */}
      <main className="w-full max-w-none mx-auto px-4 md:px-6 py-8 md:py-12 relative z-0">
        {/* Floating Container */}
        <div className="bg-gray-50 rounded-2xl shadow-sm border-0 p-6 md:p-12 mb-12 md:mb-20 relative z-0" style={{boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'}}>


          {/* Online Booking Portal */}
          {selectedBusiness && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-8">
              {/* Portal Header */}
              <div className="border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 rounded-t-xl sm:rounded-t-2xl bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                  <div className="flex-1">
                    <h4 className="text-base sm:text-xl font-bold text-gray-900">
                      {selectedBusiness.name || 'Online Booking Portal'}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">
                      Online Booking Portal
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    {/* Preview and Copy Link - Side by side */}
                    <div className="flex space-x-2 sm:space-x-3">
                      <button 
                        onClick={() => {
                          if (selectedBusiness?.slug) {
                            const bookingUrl = `${getSiteUrl()}/${selectedBusiness.slug}/book`;
                            window.open(bookingUrl, '_blank');
                          }
                        }}
                        className="flex-1 sm:flex-none px-2.5 py-1.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg sm:rounded-md hover:bg-gray-200 transition-all duration-200 active:scale-95"
                      >
                        Preview
                      </button>
                      <button 
                        onClick={() => {
                          if (selectedBusiness?.slug) {
                            const bookingUrl = `${getSiteUrl()}/${selectedBusiness.slug}/book`;
                            navigator.clipboard.writeText(bookingUrl);
                            toast.success('Booking URL copied to clipboard!');
                          }
                        }}
                        className="flex-1 sm:flex-none px-2.5 py-1.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg sm:rounded-md hover:bg-gray-200 transition-all duration-200 active:scale-95"
                      >
                        Copy Link
                      </button>
                    </div>
                    
                    {/* Toggle Switch */}
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={selectedBusiness.booking_settings?.allow_online_bookings || false}
                        onChange={handleToggleOnlineBookings}
                        className={`${
                          selectedBusiness.booking_settings?.allow_online_bookings ? 'bg-purple-600' : 'bg-gray-200'
                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2`}
                      >
                        <span
                          className={`${
                            selectedBusiness.booking_settings?.allow_online_bookings ? 'translate-x-6' : 'translate-x-1'
                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                      <span className="text-xs text-gray-700">
                        {selectedBusiness.booking_settings?.allow_online_bookings ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Portal Content - Hidden on mobile */}
              <div className="hidden sm:block p-3 sm:p-6">
                <p className="text-sm text-gray-600">
                  Allow customers to book services through your online portal
                </p>
                {selectedBusiness?.slug && (
                  <div className="mt-3 text-xs">
                    <span className="text-gray-500 font-semibold">Booking URL: </span>
                    <div className="mt-1.5">
                      <code className="bg-gray-50 px-2 py-1.5 rounded-lg text-purple-600 text-xs break-all shadow-sm border border-gray-200">
                        {getSiteUrl()}/{selectedBusiness.slug}/book
                      </code>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Categories Section */}
          <div className="bg-white rounded-2xl border-0 mb-8" style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}>
            <div className="p-6 md:p-8">
              <div className="flex flex-col space-y-8">
                <div className="w-full">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900">
                      {selectedCategoryForEdit ? `Edit Category: ${selectedCategoryForEdit.category_title}` : 'Create Your Online Booking Flow'}
                    </h3>
                    {selectedCategoryForEdit && (
                      <button
                        onClick={handleBackToList}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
                      >
                        ← Back to Categories
                      </button>
                    )}
                  </div>
                  
                  {/* Show Create Category button when not editing */}
                  {!selectedCategoryForEdit && (
                    <>
                      <div className="flex flex-col sm:flex-row gap-3 mb-6">
                        <button
                          onClick={() => setIsModalOpen(true)}
                          className="inline-flex items-center px-4 py-3 text-sm font-medium text-white bg-purple-700 rounded-2xl hover:bg-purple-600 transition-all duration-200 active:scale-95"
                          style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create Category
                        </button>
                        
                        {/* Edit Online Booking - Only show in development mode */}
                        {process.env.NODE_ENV === 'development' && (
                          <button
                            onClick={() => {
                              navigate('/online-booking-editor');
                            }}
                            className="inline-flex items-center px-4 py-3 text-sm font-medium text-purple-700 bg-purple-100 border border-purple-300 rounded-2xl hover:bg-purple-200 transition-all duration-200 active:scale-95"
                            style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
                          >
                            <SettingsIcon className="h-4 w-4 mr-2" />
                            Edit Online Booking
                          </button>
                        )}
                      </div>
                      
                      {/* Category List */}
                      {categories.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-md font-semibold text-gray-700 mb-3">Categories</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {categories.map(category => (
                              <div
                                key={category.id}
                                className="relative bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors cursor-pointer group"
                                onClick={() => handleCategorySelect(category)}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCategory(category.id);
                                  }}
                                  className="absolute top-2 right-2 p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600 transition-all duration-200 opacity-0 group-hover:opacity-100"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                                <div>
                                  <h5 className="font-medium text-gray-900 mb-1">{category.category_title}</h5>
                                  <p className="text-sm text-gray-500">
                                    {category.sub_categories?.length || 0} subcategories
                                  </p>
                                  <div className="mt-2 text-xs text-purple-600">
                                    Click to edit →
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Single Category Flow Editor */}
                {selectedCategoryForEdit && (
                  <div className="flex justify-center w-full overflow-x-auto pb-4">
                    <div className="flex flex-col items-center space-y-8 min-w-max pb-4">
                      <div className="flex flex-col items-center">
                        <div className="relative flex items-center">
                          <span className="inline-flex items-center px-6 py-3 rounded-md text-sm font-medium bg-gray-100 text-gray-800 border border-black">
                            {selectedCategoryForEdit.category_title}
                          </span>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="w-px h-3 bg-gray-300"></div>
                          {!selectedCategoryForEdit.sub_categories?.length && (
                            <button
                              onClick={() => handlePlusClick(selectedCategoryForEdit.id)}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-900 text-white hover:bg-gray-800 my-1"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          )}
                          <div className="w-px h-3 bg-gray-300"></div>
                        </div>
                        {/* Sub-categories */}
                        {selectedCategoryForEdit.sub_categories && selectedCategoryForEdit.sub_categories.length > 0 && (
                          <div className="flex flex-col items-center">
                            {selectedCategoryForEdit.sub_categories.map((subCategory, index) => (
                              <div key={index} className="flex flex-col items-center">
                                <div className="relative flex items-center w-full">
                                  <button
                                    onClick={() => handleDeleteSubCategory(subCategory)}
                                    className="absolute -top-2 -right-2 p-1.5 bg-gray-900 rounded-full text-white hover:bg-gray-800 transition-colors z-20"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                  <span className="w-40 min-w-[10rem] px-4 py-2 text-sm bg-blue-100 text-black rounded-md flex justify-center border border-black">
                                    {subCategory.title}
                                  </span>
                                </div>
                                <div className="flex flex-col items-center space-y-2 w-40 min-w-[10rem]">
                                  <div className="w-px h-1 bg-gray-300"></div>
                                  <button
                                    onClick={() => {
                                      setSelectedCategoryId(selectedCategoryForEdit.id);
                                      setSelectedSubCategory(subCategory);
                                      setIsSelectServiceModalOpen(true);
                                    }}
                                    className="w-full px-3 py-2 text-xs md:py-0.5 font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                                  >
                                    Assign Services
                                  </button>
                                  {/* Display assigned services */}
                                  {subCategory.services.length > 0 && (
                                    <div className="flex flex-col items-center space-y-2 mt-1 w-full">
                                      {subCategory.services.map((service, serviceIndex) => (
                                        <div key={serviceIndex} className="relative w-full">
                                          <button
                                            onClick={() => handleDeleteService(subCategory, service.service)}
                                            className="absolute -top-2 -right-2 p-1.5 bg-gray-900 rounded-full text-white hover:bg-gray-800 transition-colors z-20"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </button>
                                          <span className="text-sm text-black px-3 py-1 bg-yellow-100 rounded flex flex-col items-center w-full border border-black relative">
                                            <button 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedServiceInfo(service);
                                                setIsServiceInfoModalOpen(true);
                                              }}
                                              className="absolute top-1 left-1 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-blue-600 z-10"
                                              title="View service details"
                                            >
                                              i
                                            </button>
                                            <button 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenModifierModal(service);
                                              }}
                                              className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-green-600 z-10"
                                              title="Add modifiers"
                                            >
                                              <Plus className="w-3 h-3" />
                                            </button>
                                            <span>{service.service.name}</span>
                                            <span className="text-xs text-gray-500">{service.priceOption.name}</span>
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="w-px h-3 bg-gray-300"></div>
                                </div>
                                {index === selectedCategoryForEdit.sub_categories!.length - 1 && (
                                  <>
                                    <div className="w-px h-3 bg-gray-300"></div>
                                    <button
                                      onClick={() => handlePlusClick(selectedCategoryForEdit.id, true)}
                                      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-900 text-white hover:bg-gray-800 my-1"
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                    <div className="w-px h-3 bg-gray-300"></div>
                                  </>
                                )}
                                {index !== selectedCategoryForEdit.sub_categories!.length - 1 && (
                                  <div className="w-px h-3 bg-gray-300"></div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Page Customization Section */}
          <div className="bg-white rounded-2xl border-0 mb-8" style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}>
            <div className="p-3 sm:p-6 lg:p-8">
              <div className="space-y-6">
                {/* Header */}
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Customize Your Online Booking Page</h3>
                  <p className="text-sm text-gray-600">Personalize the appearance of your booking page to match your brand</p>
                </div>
                
                {/* Booking Page Settings - Collapsed by default */}
                <details className="border border-gray-200 rounded-lg sm:rounded-2xl shadow-sm">
                  <summary className="px-3 sm:px-5 py-2.5 sm:py-3 bg-gray-50 cursor-pointer text-xs sm:text-sm font-bold sm:font-medium text-gray-900 rounded-t-lg sm:rounded-t-2xl hover:bg-gray-100 transition-all duration-200 active:scale-[0.99]">
                    Booking Page Settings & Styling
                  </summary>
                  <div className="p-2.5 sm:p-5 space-y-3 sm:space-y-4 bg-white rounded-b-lg sm:rounded-b-2xl">
                    {/* Page Branding */}
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-3">Page Branding</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* Logo Upload */}
                        <BusinessLogoUpload size="small" />

                        {/* Navigation Color */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Navigation Bar Color
                          </label>
                          <div className="flex items-center space-x-2">
                            {/* Mobile: Color button that opens modal */}
                            <button
                              onClick={() => openColorPicker('navbar', navbarColor)}
                              className="sm:hidden w-8 h-8 rounded border border-gray-300 flex items-center justify-center"
                              style={{ backgroundColor: navbarColor }}
                            >
                              <span className="sr-only">Select navbar color</span>
                            </button>
                            
                            {/* Desktop: Native color picker */}
                            <input
                              type="color"
                              value={navbarColor}
                              onChange={(e) => setNavbarColor(e.target.value)}
                              className="hidden sm:block w-6 h-6 rounded border border-gray-300"
                            />
                            <input
                              type="text"
                              value={navbarColor}
                              onChange={(e) => setNavbarColor(e.target.value)}
                              className="hidden sm:flex flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                              placeholder="#000000"
                            />
                            <button
                              onClick={handleSaveNavbarColor}
                              className="hidden sm:inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors"
                            >
                              Save
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Color for the top navigation bar on your booking page
                          </p>
                        </div>
                      </div>
                      
                      {/* Logo Style Setting */}
                      {selectedBusiness?.booking_settings?.logo_url && (
                        <div className="mt-4">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Logo Style
                          </label>
                          <button
                            onClick={() => {
                              console.log('Logo invert button clicked. Current value:', logoInvert);
                              const newValue = !logoInvert;
                              console.log('Setting new value:', newValue);
                              setLogoInvert(newValue);
                              handleSaveLogoInvert(newValue);
                            }}
                            className={`w-full px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium transition-colors ${
                              logoInvert 
                                ? 'bg-purple-100 border-purple-300 text-purple-800' 
                                : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {logoInvert ? 'White Logo (for dark backgrounds)' : 'Original Colors'}
                          </button>
                          <p className="text-xs text-gray-500 mt-1">
                            Make your logo white when using dark navigation bar colors
                          </p>
                        </div>
                      )}
                      
                      {/* Call Button in Header Settings */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs sm:text-sm font-medium text-gray-700">
                            Enable Call Button in Header
                          </label>
                          <Switch
                            checked={popupSettings.call_button_enabled}
                            onChange={(enabled) => handlePopupSettingChange('call_button_enabled', enabled)}
                            disabled={!popupSettingsLoaded}
                            className={`${
                              popupSettings.call_button_enabled ? 'bg-purple-600' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50`}
                          >
                            <span
                              className={`${
                                popupSettings.call_button_enabled ? 'translate-x-6' : 'translate-x-1'
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                        <p className="text-xs text-gray-500">
                          Display a call button in the header of your online booking page so customers can easily contact you
                        </p>
                      </div>
                    </div>

                    {/* Booking Configuration */}
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-3">Booking Configuration</p>
                      <div className="space-y-4">
                        {/* Buffer Time */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Buffer Time
                          </label>
                          <div className="max-w-xs">
                            <select
                              value={bufferTime}
                              onChange={(e) => handleBufferTimeChange(Number(e.target.value))}
                              disabled={!bufferTimeLoaded}
                              className="block w-full px-2.5 py-2 sm:px-3 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md text-xs sm:text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm disabled:bg-gray-50 disabled:text-gray-500"
                            >
                              {!bufferTimeLoaded ? (
                                <option value="0">Loading...</option>
                              ) : (
                                <>
                                  <option value="0">No buffer time</option>
                                  <option value="30">30 minutes</option>
                                  <option value="60">1 hour</option>
                                  <option value="90">1.5 hours</option>
                                  <option value="120">2 hours</option>
                                </>
                              )}
                            </select>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Buffer time between mobile bookings to allow for travel time
                          </p>
                        </div>

                        {/* Pop Up Window Settings */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-xs sm:text-sm font-medium text-gray-700">
                              Pop Up Window
                            </label>
                            <Switch
                              checked={popupSettings.popup_enabled}
                              onChange={handlePopupToggle}
                              disabled={!popupSettingsLoaded}
                              className={`${
                                popupSettings.popup_enabled ? 'bg-purple-600' : 'bg-gray-200'
                              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50`}
                            >
                              <span
                                className={`${
                                  popupSettings.popup_enabled ? 'translate-x-6' : 'translate-x-1'
                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                              />
                            </Switch>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">
                            Show a promotional popup message to customers on your booking page
                          </p>

                          {/* Popup Settings - Only show when enabled */}
                          {popupSettings.popup_enabled && popupSettingsLoaded && (
                            <div className="space-y-3 pl-4 border-l-2 border-purple-100">
                              {/* Time before popup */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Time before pop up (seconds)
                                </label>
                                <div className="max-w-xs">
                                  <input
                                    type="number"
                                    min="0"
                                    max="60"
                                    value={popupSettings.popup_delay_seconds}
                                    onChange={(e) => handlePopupSettingChange('popup_delay_seconds', parseInt(e.target.value) || 0)}
                                    className="block w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                                  />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  How long to wait before showing the popup (0-60 seconds)
                                </p>
                              </div>

                              {/* Popup duration */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Pop up duration (seconds)
                                </label>
                                <div className="max-w-xs">
                                  <input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={popupSettings.popup_duration_seconds}
                                    onChange={(e) => handlePopupSettingChange('popup_duration_seconds', parseInt(e.target.value) || 5)}
                                    className="block w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                                  />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  How long the popup stays visible (1-30 seconds)
                                </p>
                              </div>

                              {/* Popup text */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Pop up message
                                </label>
                                <textarea
                                  value={popupSettings.popup_text}
                                  onChange={(e) => handlePopupSettingChange('popup_text', e.target.value)}
                                  rows={3}
                                  maxLength={200}
                                  className="block w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm resize-none"
                                  placeholder="Enter your promotional message..."
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                  Message to display in the popup (max 200 characters)
                                </p>
                              </div>

                              {/* Button text */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Button text
                                </label>
                                <div className="max-w-xs">
                                  <input
                                    type="text"
                                    value={popupSettings.popup_button_text}
                                    onChange={(e) => handlePopupSettingChange('popup_button_text', e.target.value)}
                                    maxLength={50}
                                    className="block w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                                    placeholder="Enter button text..."
                                  />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  Text to display on the popup button (max 50 characters)
                                </p>
                              </div>

                              {/* Button action */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Button action
                                </label>
                                <div className="max-w-xs">
                                  <select
                                    value={popupSettings.popup_button_action}
                                    onChange={(e) => handlePopupSettingChange('popup_button_action', e.target.value)}
                                    className="block w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                                  >
                                    <option value="url">Redirect to URL</option>
                                    <option value="lead_form">Show lead capture form</option>
                                  </select>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  What happens when customers click the button
                                </p>
                              </div>

                              {/* Button URL - Only show when action is URL */}
                              {popupSettings.popup_button_action === 'url' && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Button URL
                                  </label>
                                  <div className="max-w-md">
                                    <input
                                      type="url"
                                      value={popupSettings.popup_button_url}
                                      onChange={(e) => handlePopupSettingChange('popup_button_url', e.target.value)}
                                      className="block w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                                      placeholder="https://example.com"
                                    />
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">
                                    URL to redirect customers when they click the button
                                  </p>
                                </div>
                              )}

                              {/* Email settings - Only show when action is lead_form */}
                              {popupSettings.popup_button_action === 'lead_form' && (
                                <div className="space-y-3 pl-4 border-l-2 border-blue-100">
                                  <div className="flex items-center justify-between">
                                    <label className="text-xs font-medium text-gray-700">
                                      Send email on form submit
                                    </label>
                                    <Switch
                                      checked={popupSettings.popup_send_email}
                                      onChange={(enabled) => handlePopupSettingChange('popup_send_email', enabled ? 'true' : 'false')}
                                      className={`${
                                        popupSettings.popup_send_email ? 'bg-purple-600' : 'bg-gray-200'
                                      } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2`}
                                    >
                                      <span
                                        className={`${
                                          popupSettings.popup_send_email ? 'translate-x-5' : 'translate-x-1'
                                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                      />
                                    </Switch>
                                  </div>
                                  <p className="text-xs text-gray-400">
                                    Automatically send an email when a customer submits the lead form
                                  </p>

                                  {/* Email template selection - Only show when send email is enabled */}
                                  {popupSettings.popup_send_email && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Email template
                                      </label>
                                      <div className="max-w-xs">
                                        <select
                                          value={popupSettings.popup_email_template_id}
                                          onChange={(e) => handlePopupSettingChange('popup_email_template_id', e.target.value)}
                                          className="block w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                                        >
                                          <option value="">Select a template</option>
                                          {emailTemplates.map((template) => (
                                            <option key={template.id} value={template.id}>
                                              {template.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <p className="text-xs text-gray-400 mt-1">
                                        Choose which email template to send to the customer
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>


                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>

      {/* Create Category Modal */}
      {isModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
              <h3 className="text-base sm:text-xl font-bold text-gray-900">Create Category</h3>
              <button 
                onClick={() => setIsModalOpen(false)} 
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveCategory();
              }}
              className="px-3 sm:px-6 py-4 sm:py-4"
            >
              <div className="mb-4 sm:mb-4">
                <label className="block text-sm sm:text-sm font-semibold sm:font-medium text-gray-700 mb-2 sm:mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={categoryTitle}
                  onChange={(e) => setCategoryTitle(e.target.value)}
                  className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md text-sm sm:text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm"
                  placeholder="Enter category title"
                  required
                  autoFocus
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 sm:pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-3 py-2.5 sm:py-2 text-sm sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg sm:rounded-md hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!categoryTitle.trim() || isLoading}
                  className="w-full sm:w-auto px-3 py-2.5 sm:py-2 text-sm sm:text-sm font-semibold text-white bg-purple-700 rounded-lg sm:rounded-md hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Options Modal */}
      <Dialog
        open={isOptionsModalOpen}
        onClose={() => setIsOptionsModalOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Add Sub-category
              </Dialog.Title>
              <button
                onClick={() => setIsOptionsModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setIsOptionsModalOpen(false);
                  setIsSubCategoryModalOpen(true);
                }}
                className="w-full px-4 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-md"
              >
                Create Sub Categories
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Sub-category Modal */}
      <Dialog
        open={isSubCategoryModalOpen}
        onClose={() => setIsSubCategoryModalOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Add Sub-category
              </Dialog.Title>
              <button
                onClick={() => setIsSubCategoryModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              <label htmlFor="subCategoryTitle" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                id="subCategoryTitle"
                value={subCategoryTitle}
                onChange={(e) => setSubCategoryTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter sub-category title"
              />
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsSubCategoryModalOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSubCategory}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Select Service Modal */}
      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => {
          setIsSelectServiceModalOpen(false);
          setSelectedSubCategory(null);
        }}
        onAdd={handleServiceAdd}
      />

      {/* Color Picker Modal */}
      <Dialog
        open={isColorPickerOpen}
        onClose={() => setIsColorPickerOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Choose Navigation Bar Color
              </Dialog.Title>
              <button
                onClick={() => setIsColorPickerOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              <div className="flex flex-col space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Select a color
                </label>
                <input
                  type="color"
                  value={navbarColor}
                  onChange={(e) => setNavbarColor(e.target.value)}
                  className="w-full h-12 p-1 rounded cursor-pointer"
                />
                
                <div className="flex items-center space-x-2 mt-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Color code
                    </label>
                    <input
                      type="text"
                      value={navbarColor}
                      onChange={(e) => {
                        // Validate hex color format
                        const isValidHex = /^#([0-9A-F]{3}){1,2}$/i.test(e.target.value);
                        if (isValidHex || e.target.value.startsWith('#')) {
                          setNavbarColor(e.target.value);
                        }
                      }}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="w-12 h-12 rounded border border-gray-300" style={{ backgroundColor: navbarColor }}></div>
                </div>
                
                <div className="mt-2">
                  <p className="text-sm text-gray-500">Preview:</p>
                  <div className="mt-2 w-full h-12 rounded-t-lg" style={{ backgroundColor: navbarColor }}></div>
                  <div className="bg-white border border-gray-300 border-t-0 h-12 rounded-b-lg flex items-center justify-center text-sm text-gray-700">
                    Booking Page Preview
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsColorPickerOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNavbarColor}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Color
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Service Info Modal */}
      <Dialog
        open={isServiceInfoModalOpen}
        onClose={() => setIsServiceInfoModalOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900 truncate max-w-xs">
                {selectedServiceInfo?.service.name || "Service Details"}
              </Dialog.Title>
              <button
                onClick={() => setIsServiceInfoModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedServiceInfo && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Service Name</h3>
                  <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.service.name}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Description</h3>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedServiceInfo.service.description || "No description available"}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Price Option</h3>
                    <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.priceOption.name}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Price</h3>
                    <p className="mt-1 text-sm text-gray-900">${selectedServiceInfo.priceOption.price.toFixed(2)}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Duration</h3>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedServiceInfo.priceOption.duration_minutes >= 60 
                        ? `${Math.floor(selectedServiceInfo.priceOption.duration_minutes / 60)} hour${selectedServiceInfo.priceOption.duration_minutes >= 120 ? 's' : ''} ${selectedServiceInfo.priceOption.duration_minutes % 60 > 0 ? `${selectedServiceInfo.priceOption.duration_minutes % 60} min` : ''}`
                        : `${selectedServiceInfo.priceOption.duration_minutes} min`
                      }
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Tax Exempt</h3>
                    <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.service.tax_exempt ? "Yes" : "No"}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsServiceInfoModalOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Color Picker Modal */}
      {colorPickerModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={closeColorPicker}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Choose {colorPickerModal.type} Color
                </h3>
                <button
                  onClick={closeColorPicker}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-7 gap-2 mb-4">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      colorPickerModal.currentColor === color 
                        ? 'border-blue-500 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  >
                    {colorPickerModal.currentColor === color && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full shadow-sm"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={colorPickerModal.currentColor}
                    onChange={(e) => setColorPickerModal(prev => ({
                      ...prev,
                      currentColor: e.target.value
                    }))}
                    className="w-10 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={colorPickerModal.currentColor}
                    onChange={(e) => setColorPickerModal(prev => ({
                      ...prev,
                      currentColor: e.target.value
                    }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="#000000"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={closeColorPicker}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleColorSelect(colorPickerModal.currentColor)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
        </div>
      </main>

      {/* Modifier Modal */}
      <ModifierModal
        isOpen={isModifierModalOpen}
        onClose={handleCloseModifierModal}
        service={selectedServiceForModifier}
      />
    </div>
  );
} 